import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import OutilService from "../../../services/smq/OutilService";
import { fieldSx, OUTIL_COLORS } from "./OutilContainer.styles";

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const EMPTY_FORM = {
  action_requise: "", categorie_swot: "", date_revision: "", enjeux: "", indicateur: "",
  observation: "", opportunite: "", partie_interessee: "", responsable: "", risque: "", type_contexte: "",
};
const SWOT_OPTIONS = [
  { label: "Force", value: "force" }, { label: "Faiblesse", value: "faiblesse" },
  { label: "Opportunité", value: "opportunite" }, { label: "Menace", value: "menace" },
];
const TYPE_OPTIONS = [{ label: "Interne", value: "interne" }, { label: "Externe", value: "externe" }];

const fileName = (path) => (String(path || "").split(/[\\/]/).pop() || "").replace(/^[a-f0-9]{24}__/, "") || "Pièce jointe";
const rowForm = (row) => ({
  action_requise: row?.action_requise || "", categorie_swot: row?.categorie_swot || "",
  date_revision: row?.date_revision ? String(row.date_revision).slice(0, 10) : "", enjeux: row?.enjeux || "",
  indicateur: row?.indicateur || "", observation: row?.observation || "", opportunite: row?.opportunite || "",
  partie_interessee: row?.partie_interessee || "", responsable: row?.responsable || "",
  risque: row?.risque || "", type_contexte: row?.type_contexte || "",
});

const Section = ({ children, title }) => (
  <Paper elevation={0} sx={{ border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: 1.5, overflow: "hidden" }}>
    <Box sx={{ bgcolor: "#f3f7fa", borderBottom: `1px solid ${OUTIL_COLORS.border}`, px: 1.5, py: 1 }}>
      <Typography color={OUTIL_COLORS.navy} fontSize={11} fontWeight={800}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1.5 }}>{children}</Box>
  </Paper>
);

const downloadBlob = (blob, name) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const ContexteOrganisationDialog = ({ mode, onClose, onSaved, open, outilEntree, processus, row }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef(null);
  const isCreate = mode === "create";
  const isView = mode === "view";
  const isValidated = row?.statut === "Validé";

  useEffect(() => {
    if (!open) return;
    setForm(isCreate ? EMPTY_FORM : rowForm(row));
    setErrors({});
    setFile(null);
    setSaving(false);
  }, [isCreate, open, row]);

  const change = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: "", submit: "" }));
  };

  const selectFile = (event) => {
    const selected = event.target.files?.[0] || null;
    event.target.value = "";
    if (selected && selected.size > MAX_FILE_SIZE) {
      setFile(null);
      setErrors((current) => ({ ...current, file: "La pièce jointe ne doit pas dépasser 20 Mo." }));
      return;
    }
    setFile(selected);
    setErrors((current) => ({ ...current, file: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.type_contexte) next.type_contexte = "Type obligatoire.";
    if (!form.categorie_swot) next.categorie_swot = "Catégorie SWOT obligatoire.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (submissionAction) => {
    if (saving || !outilEntree?.outil_entree_id || !validate()) return;
    setSaving(true);
    try {
      const data = new FormData();
      data.append("outil_entree_id", outilEntree.outil_entree_id);
      data.append("processus_id", processus.processus_id);
      data.append("submission_action", submissionAction);
      Object.entries(form).forEach(([key, value]) => data.append(key, value ?? ""));
      if (file) data.append("file", file);
      const response = isCreate
        ? await OutilService.createContexte(data)
        : await OutilService.updateContexte(row.contexte_id, data);
      await onSaved(response.message || "Contexte sauvegardé.");
    } catch (error) {
      setErrors((current) => ({ ...current, submit: error.message }));
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (!row?.contexte_id || downloading) return;
    setDownloading(true);
    try {
      const response = await OutilService.downloadContexteFile(row.contexte_id);
      downloadBlob(response.data, fileName(row.file));
    } catch (error) {
      setErrors((current) => ({ ...current, submit: error.message }));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="lg" onClose={() => !saving && onClose()} open={open} scroll="paper">
      <DialogTitle sx={{ color: OUTIL_COLORS.navy, fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700 }}>
        Contexte de l’organisme — {isCreate ? "Nouvelle fiche" : `Fiche #${row?.contexte_id || ""}`}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc", display: "grid", gap: 1.5, p: { xs: 1.25, sm: 2 } }}>
        {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
        <Box sx={{ bgcolor: "#edf3f8", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            Processus : <strong>{processus?.instance || "—"}</strong> · Fiche documentaire : <strong>{outilEntree?.outil_code || "—"}</strong>
          </Typography>
        </Box>

        <Section title="PIÈCE JOINTE">
          <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1 }}>
            <input hidden onChange={selectFile} ref={inputRef} type="file" />
            {!isView && <Button disabled={saving} onClick={() => inputRef.current?.click()} size="small" startIcon={<AttachFileIcon />} variant="outlined">{row?.file ? "Remplacer" : "Ajouter une pièce jointe"}</Button>}
            {file && <Typography fontSize={10.5}>{file.name}</Typography>}
            {!file && row?.file && <Button disabled={downloading} onClick={download} size="small" startIcon={downloading ? <CircularProgress size={14} /> : <DownloadOutlinedIcon />}>{fileName(row.file)}</Button>}
          </Box>
          {errors.file && <Typography color="error" fontSize={10}>{errors.file}</Typography>}
        </Section>

        <Section title="IDENTIFICATION">
          <Grid container spacing={1.5}>
            <Grid item md={4} xs={12}><TextField disabled fullWidth label="Processus cible" size="small" sx={fieldSx} value={processus?.instance || ""} /></Grid>
            <Grid item md={4} xs={12}><TextField disabled={isView || saving} error={Boolean(errors.type_contexte)} fullWidth helperText={errors.type_contexte} label="Type *" onChange={change("type_contexte")} select size="small" sx={fieldSx} value={form.type_contexte}>{TYPE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</TextField></Grid>
            <Grid item md={4} xs={12}><TextField disabled={isView || saving} error={Boolean(errors.categorie_swot)} fullWidth helperText={errors.categorie_swot} label="Catégorie SWOT *" onChange={change("categorie_swot")} select size="small" sx={fieldSx} value={form.categorie_swot}>{SWOT_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</TextField></Grid>
          </Grid>
        </Section>

        <Section title="ANALYSE">
          <Grid container spacing={1.5}>
            <Grid item xs={12}><TextField disabled={isView || saving} fullWidth label="Enjeux identifiés" multiline minRows={2} onChange={change("enjeux")} size="small" sx={fieldSx} value={form.enjeux} /></Grid>
            <Grid item xs={12}><TextField disabled={isView || saving} fullWidth label="Parties intéressées concernées" multiline minRows={2} onChange={change("partie_interessee")} size="small" sx={fieldSx} value={form.partie_interessee} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Risques associés" multiline minRows={2} onChange={change("risque")} size="small" sx={fieldSx} value={form.risque} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Opportunités associées" multiline minRows={2} onChange={change("opportunite")} size="small" sx={fieldSx} value={form.opportunite} /></Grid>
          </Grid>
        </Section>

        <Section title="ACTIONS & SUIVI">
          <Grid container spacing={1.5}>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Action à mener" multiline minRows={2} onChange={change("action_requise")} size="small" sx={fieldSx} value={form.action_requise} /></Grid>
            <Grid item md={6} xs={12}><TextField disabled={isView || saving} fullWidth label="Responsable" multiline minRows={2} onChange={change("responsable")} size="small" sx={fieldSx} value={form.responsable} /></Grid>
            <Grid item md={4} xs={12}><TextField disabled={isView || saving} fullWidth label="Indicateur" onChange={change("indicateur")} size="small" sx={fieldSx} value={form.indicateur} /></Grid>
            <Grid item md={4} xs={12}><TextField disabled={isView || saving} fullWidth InputLabelProps={{ shrink: true }} label="Date de révision" onChange={change("date_revision")} size="small" sx={fieldSx} type="date" value={form.date_revision} /></Grid>
            <Grid item md={4} xs={12}><TextField disabled fullWidth label="Statut" size="small" sx={fieldSx} value={row?.statut || "Brouillon"} /></Grid>
            <Grid item xs={12}><TextField disabled={isView || saving} fullWidth label="Observation" multiline minRows={2} onChange={change("observation")} size="small" sx={fieldSx} value={form.observation} /></Grid>
          </Grid>
        </Section>
      </DialogContent>
      <DialogActions sx={{ gap: 0.75, px: 2, py: 1.25 }}>
        <Button disabled={saving} onClick={onClose}>{isView ? "Fermer" : "Annuler"}</Button>
        {!isView && <Button disabled={saving} onClick={() => submit("save")} startIcon={<SaveOutlinedIcon />} variant="outlined">Sauvegarder uniquement</Button>}
        {!isView && <Button disabled={saving || isValidated} onClick={() => submit("validate")} startIcon={<VerifiedOutlinedIcon />} variant="contained">{isValidated ? "Déjà validé" : "Valider & Sauvegarder"}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default ContexteOrganisationDialog;
